import { MutationCache, QueryClient, type Query } from '@tanstack/react-query'
import { errorMessage, errorStatus } from '@/lib/apiError'

/**
 * Tuned for the BIGROI HRMS SPA — minimize unnecessary refetches.
 *  - staleTime 5min: matches a typical admin session's read pattern; avoids re-hitting endpoints when navigating back.
 *  - gcTime 30min: keep results around long enough that tab-switching never re-fetches.
 *  - refetchOnWindowFocus / Reconnect OFF; refetchOnMount only for STALE data, so an `invalidateQueries`
 *    after a mutation reaches pages that were not mounted at the time.
 *  - retry skips 401/403/404 (no point retrying perm/missing); other errors retry twice with exponential backoff.
 *  - networkMode 'always': don't pause queries during a transient network blip.
 *
 * Errors are never silent (spec 21 §3.9.3, G-16):
 *  - A query that fails with NO data to show throws to the nearest error boundary
 *    (`AreaOutlet`), which renders an error panel with Retry and the trace id instead of
 *    the page's empty state. A page that renders its own inline error opts out with
 *    `meta: { inlineError: true }`; a refetch failure over data already on screen keeps the
 *    data. Shell-level queries (session, notifications, badges) never take a page down.
 *  - A mutation that fails without its own `onError` is announced through
 *    `MUTATION_ERROR_EVENT`, so a failed save can never look like nothing happened.
 */

/** Query-key roots owned by the app shell: they must never blank the page they sit on. */
const SHELL_KEYS = new Set(['auth', 'notifications', 'approvals-count'])

function shouldThrow(error: unknown, query: Pick<Query, 'meta' | 'queryKey' | 'state'>): boolean {
  if (query.meta?.inlineError) return false
  if (SHELL_KEYS.has(String(query.queryKey[0]))) return false
  const status = errorStatus(error)
  // 401 is the session guard's job (RequireAuth / login redirect), not a page error.
  if (status === 401 || status === 419) return false
  return query.state.data === undefined
}

/**
 * Fired for a failed mutation that has no `onError` of its own. AppShell turns
 * it into an error toast. Many list pages wire `onSuccess` only, so a failed or
 * partially-failed bulk delete used to vanish without a word (spec 22 §2.3).
 */
export const MUTATION_ERROR_EVENT = 'bigroi:mutation-error'
export interface MutationErrorDetail { message: string; title?: string }

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { inlineError?: boolean }
    mutationMeta: { silentError?: boolean }
  }
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => {
      if (mutation.options.onError || mutation.meta?.silentError) return // the page reports it itself
      if (typeof window === 'undefined') return
      const e = err as { name?: string }
      const detail: MutationErrorDetail = {
        // Plain-language text from the API envelope (first field message on a 422).
        message: errorMessage(err) || 'The action failed.',
        title: e?.name === 'BulkPartialFailure' ? 'Bulk delete incomplete' : 'Action failed',
      }
      window.dispatchEvent(new CustomEvent(MUTATION_ERROR_EVENT, { detail }))
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime:    30 * 60 * 1000,
      refetchOnWindowFocus: false,
      // true = refetch on mount ONLY when the cached data is stale (invalidated, or older than
      // staleTime). With `false`, a query invalidated while its page was not mounted was never
      // refetched, so e.g. a template created on one tab was missing from the next (T4-091).
      refetchOnMount: true,
      refetchOnReconnect: false,
      networkMode: 'always',
      retry: (failureCount, err: unknown) => {
        const status = errorStatus(err)
        if (status === 401 || status === 403 || status === 404 || status === 419 || status === 422) return false
        // QA-M01: a pending database update will not fix itself in seconds; retrying only delays the notice.
        if (status === 503 && (err as { details?: { reason?: string } })?.details?.reason === 'DATABASE_UPDATE_PENDING') return false
        return failureCount < 2
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      throwOnError: shouldThrow,
    },
    mutations: {
      retry: false,
    },
  },
})
